/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { useLazyGetPlatformCatalogQuery } from "@api/catalogs";
import { PlatformCatalogItem } from "@interfaces/catalogs";
import { useDispatch, useSelector } from "react-redux";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { setMotorbikeInformation } from "@store/slices/registerSlice";
import { useNavigate } from "react-router-dom";
import { RootState } from "@store/store";
import { usePostValidateNextStageMutation, usePutMotorbikeDetailMutation } from "@api/applications";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { useLazyPalencaGigWidgetUrlQuery } from "@api/sat_apis_new/satApsi";
import CenteredLoader from "@components/CenteredLoader";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

const PLATFORM_CODE_OTHER = "PCOTR";
const SCREEN_STATUS = "CSIP1";

const COMPANYS_WITHOUT_MOTODETAILS = ["micredimoto"];

type FormValues = {
  platformId: string;
  otherPlatformName: string;
  driverId: string;
  authorize: boolean;
};

const InformacionPlataforma = () => {
  // hooks
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const interfaceConfig = useInterfaceConfig();

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);

  // api hooks
  const [getPlatformCatalog] = useLazyGetPlatformCatalogQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [putMotorbikeDetail] = usePutMotorbikeDetailMutation();
  const [getWidgetUrl] = useLazyPalencaGigWidgetUrlQuery();

  // state
  const [platformCatalog, setPlatformCatalog] = useState<PlatformCatalogItem[]>([]);
  const [otherPlatformItem, setOtherPlatformItem] = useState<PlatformCatalogItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModalConnectPlatform, setShowModalConnectPlatform] = useState(false);
  const [widgetUrl, setWidgetUrl] = useState<string>("");

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        platformId: yup.string().required("Campo requerido"),
        otherPlatformName: yup.string().when("platformId", {
          is: (val: string) => otherPlatformItem !== null && val === otherPlatformItem.id,
          then: (schema) => schema.required("Campo requerido"),
          otherwise: (schema) => schema.notRequired(),
        }),
        driverId: yup.string().required("Campo requerido"),
        authorize: yup.boolean().oneOf([true], "Debes autorizar para continuar"),
      }),
    [otherPlatformItem],
  );

  const continueToNextStep = async () => {
    if (!nextStatus) {
      throw new Error("No se encontró el siguiente status");
    }

    await triggerPostNextStage({
      applicationId: application.id,
      statusCode: nextStatus?.code,
      username: `TOFU - ${accountUser.email}`,
    }).unwrap();

    navigate(nextStatus.url);
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      platformId: motorbikeInfo.details_platform?.platform_id ?? "",
      otherPlatformName: motorbikeInfo.details_platform?.other_platform_name ?? "",
      driverId: motorbikeInfo.details_platform?.driver_id ?? "",
      authorize: false,
    },
    validationSchema,
    onSubmit: async () => {
      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        const platformItem = platformCatalog.find((p) => p.id === formik.values.platformId);
        if (!platformItem) {
          throw new Error("Plataforma no encontrada");
        }

        setIsSubmitting(true);

        const otherPlatformName =
          formik.values.platformId === otherPlatformItem?.id ? formik.values.otherPlatformName : "";

        if (validationByCompany()) {
          if (!motorbikeInfo.motorbikeDetailId) {
            throw new Error("Motorbike detail not found");
          }
          // update motorbike detail
          await putMotorbikeDetail({
            solicitudId: application.id,
            motorbikeDetailId: motorbikeInfo.motorbikeDetailId,
            body: {
              details_platform: {
                platform_id: formik.values.platformId ?? "",
                other_platform_name: otherPlatformName ?? "",
                driver_id: formik.values.driverId ?? "",
              },
            },
          }).unwrap();

          dispatch(
            setMotorbikeInformation({
              details_platform: {
                platform_id: formik.values.platformId ?? "",
                other_platform_name: otherPlatformName ?? "",
                driver_id: formik.values.driverId ?? "",
              },
            }),
          );
        }

        if (platformItem.widget_id) {
          const response_widget_url = await getWidgetUrl({
            loan_application_id: application.id,
            plattform: platformItem.widget_id,
          }).unwrap();

          if (response_widget_url?.widget_url) {
            const widget_url = response_widget_url.widget_url;
            setWidgetUrl(widget_url);
            setShowModalConnectPlatform(true);
            return;
          }
        }

        await continueToNextStep();
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionPlataforma-onSubmit",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!nextStatus) {
        return;
      }

      // check if the user is already connected to the platform
      const urlParams = new URLSearchParams(window.location.search);
      const connected = urlParams.get("connected");
      const isConnected = connected === "true";
      if (isConnected) {
        await continueToNextStep();
        return;
      }

      // get the platform catalog
      const catalogPlataformResp = await getPlatformCatalog().unwrap();

      // find the platform with the code "other"
      const platformOther = catalogPlataformResp.find((p) => p.code === PLATFORM_CODE_OTHER);

      setPlatformCatalog(catalogPlataformResp);
      setOtherPlatformItem(platformOther ?? null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "InformacionPlataforma-fetchData" },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [getPlatformCatalog, dispatch, nextStatus]);

  const handleOnClickCloseWidgetUrl = () => {
    if (!widgetUrl) return;
    window.open(widgetUrl, "_self");
    setWidgetUrl("");
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validationByCompany = () => {
    return COMPANYS_WITHOUT_MOTODETAILS.includes(interfaceConfig.name);
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />

        {isLoading ? (
          <CenteredLoader />
        ) : (
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              fontWeight={500}
              textAlign="center"
              color={"primary.dark"}
            >
              Información de tu plataforma
            </Typography>

            <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
              <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
            </Container>

            <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
              <form onSubmit={formik.handleSubmit} noValidate>
                <FormControl
                  fullWidth
                  sx={{ mt: 2 }}
                  error={formik.touched.platformId && Boolean(formik.errors.platformId)}
                  disabled={isLoading || isSubmitting}
                >
                  <InputLabel id="plataforma-label" required>
                    Plataforma en la que trabajas
                  </InputLabel>
                  <Select
                    labelId="plataforma-label"
                    id="platformId"
                    name="platformId"
                    fullWidth
                    required
                    label="Plataforma en la que trabajas"
                    value={formik.values.platformId}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      formik.handleChange(e);
                      if (otherPlatformItem === null || selectedValue !== otherPlatformItem.id) {
                        formik.setFieldValue("otherPlatformName", "");
                      }
                    }}
                    error={formik.touched.platformId && Boolean(formik.errors.platformId)}
                    disabled={isLoading || isSubmitting}
                  >
                    <MenuItem value="">Selecciona</MenuItem>
                    {platformCatalog.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.platformId && Boolean(formik.errors.platformId) && (
                    <FormHelperText>{formik.errors.platformId}</FormHelperText>
                  )}
                </FormControl>
                {formik.values.platformId !== null && formik.values.platformId === otherPlatformItem?.id && (
                  <TextField
                    id="otherPlatformName"
                    name="otherPlatformName"
                    label="¿Cuál plataforma?"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.otherPlatformName}
                    error={formik.touched.otherPlatformName && Boolean(formik.errors.otherPlatformName)}
                    helperText={formik.touched.otherPlatformName ? formik.errors.otherPlatformName : ""}
                    disabled={isSubmitting}
                    inputProps={{ maxLength: 80 }}
                  />
                )}
                <TextField
                  id="driverId"
                  name="driverId"
                  label="Número de Driver o ID"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.driverId}
                  error={formik.touched.driverId && Boolean(formik.errors.driverId)}
                  helperText={formik.touched.driverId ? formik.errors.driverId : ""}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 50 }}
                />
                <Box mt={1}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      sx={{ alignItems: "flex-start", color: "primary.dark" }}
                      control={
                        <Checkbox
                          checked={formik.values.authorize}
                          onChange={(_, checked) => formik.setFieldValue("authorize", checked)}
                          disabled={isSubmitting}
                        />
                      }
                      label={
                        <Typography variant={isMobileOrTablet ? "body3" : "body2"} sx={{ lineHeight: 1.25 }}>
                          Autorizo que mi plataforma retenga el monto diario de pago del crédito y pague directamente a
                          MiCrediMoto.
                        </Typography>
                      }
                    />
                    {formik.touched.authorize && Boolean(formik.errors.authorize) && (
                      <FormHelperText error>{formik.errors.authorize}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                  <Button type="submit" variant="default" disabled={!formik.isValid || isSubmitting}>
                    Continuar
                  </Button>
                </Box>
              </form>
            </Container>
          </Box>
        )}
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <Dialog
        open={Boolean(showModalConnectPlatform)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="platform-connect-dialog-title"
      >
        <DialogTitle id="platform-connect-dialog-title" sx={{ position: "relative", pr: 5, pt: 2 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Typography
              variant={isMobileOrTablet ? "h6" : "h5"}
              fontWeight={600}
              color="primary.dark"
              component="span"
              textAlign="center"
            >
              Conecta con los datos de tu plataforma
            </Typography>
          </Box>

          <IconButton
            aria-label="cerrar"
            onClick={() => setShowModalConnectPlatform(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography variant={isMobileOrTablet ? "body2" : "body1"} color="primary.dark" sx={{ mb: 2 }}>
            Para continuar con tu solicitud, autoriza el acceso seguro a la información de tu cuenta en la app de
            repartidor. Así validamos tu actividad sin que tengas que subir comprobantes manualmente.
          </Typography>
          <Typography variant={isMobileOrTablet ? "body3" : "body2"} color="text.secondary" sx={{ mb: 3 }}>
            Al pulsar el botón se abrirá el sitio de nuestro proveedor de conexión en una nueva pestaña. Cuando
            termines, puedes volver aquí y continuar con el formulario.
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Button variant="contained" color="primary" onClick={handleOnClickCloseWidgetUrl}>
              Conectar con la plataforma
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default InformacionPlataforma;
